<template>
  <div class="w-full px-4">
    <!-- Desktop: Horizontal layout -->
    <div class="hidden md:block">
      <div class="flex items-start justify-between max-w-5xl mx-auto">
        <template v-for="(step, index) in steps" :key="index">
          <!-- Step Column -->
          <div class="flex flex-col items-center w-52">
            <!-- Step Card -->
            <div class="relative bg-white dark:bg-gray-700 rounded-2xl shadow-lg p-6 w-full transition-transform hover:scale-105 hover:shadow-xl">
              <!-- Color bar -->
              <div class="absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl" :class="step.barColor"></div>
              
              <!-- Icon -->
              <div class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" :class="step.iconBg">
                <font-awesome-icon :icon="step.icon" class="text-2xl" :class="step.iconColor" />
              </div>
              
              <!-- Title -->
              <h4 class="text-lg font-bold text-gray-800 dark:text-white text-center mb-2">
                {{ step.title }}
              </h4>
              
              <!-- Description -->
              <p class="text-sm text-gray-600 dark:text-gray-300 text-center min-h-[40px]">
                {{ step.description }}
              </p>
              
              <!-- Step number badge -->
              <div 
                class="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg"
                :class="step.badgeColor"
              >
                {{ index + 1 }}
              </div>
            </div>
            
            <!-- Label below badge -->
            <span class="text-sm font-medium text-gray-500 dark:text-gray-400 mt-6">
              {{ step.label }}
            </span>
          </div>
          
          <!-- Arrow (except last) -->
          <div v-if="index < steps.length - 1" class="flex-shrink-0 mt-24 mx-2">
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
        </template>
      </div>
    </div>
    
    <!-- Mobile: Vertical layout -->
    <div class="md:hidden space-y-4">
      <div v-for="(step, index) in steps" :key="'mobile-' + index" class="relative">
        <!-- Step Card -->
        <div class="bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 flex items-center gap-4">
          <!-- Number badge -->
          <div 
            class="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
            :class="step.badgeColor"
          >
            {{ index + 1 }}
          </div>
          
          <!-- Icon -->
          <div class="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" :class="step.iconBg">
            <font-awesome-icon :icon="step.icon" class="text-xl" :class="step.iconColor" />
          </div>
          
          <!-- Content -->
          <div class="flex-1 min-w-0">
            <h4 class="text-base font-bold text-gray-800 dark:text-white truncate">
              {{ step.title }}
            </h4>
            <p class="text-sm text-gray-600 dark:text-gray-300">
              {{ step.description }}
            </p>
          </div>
        </div>
        
        <!-- Connector line (except last) -->
        <div v-if="index < steps.length - 1" class="absolute left-7 top-full h-4 w-0.5 bg-gray-300 dark:bg-gray-600"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const steps = computed(() => [
  {
    icon: 'fa-solid fa-file-lines',
    iconColor: 'text-blue-600 dark:text-blue-400',
    title: t('charts.process.step1_title'),
    description: t('charts.process.step1_desc'),
    label: t('charts.process.label1'),
    barColor: 'bg-gradient-to-r from-blue-500 to-blue-600',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    badgeColor: 'bg-gradient-to-br from-blue-500 to-blue-700'
  },
  {
    icon: 'fa-solid fa-folder-open',
    iconColor: 'text-orange-600 dark:text-orange-400',
    title: t('charts.process.step2_title'),
    description: t('charts.process.step2_desc'),
    label: t('charts.process.label2'),
    barColor: 'bg-gradient-to-r from-orange-500 to-orange-600',
    iconBg: 'bg-orange-100 dark:bg-orange-900/50',
    badgeColor: 'bg-gradient-to-br from-orange-500 to-orange-700'
  },
  {
    icon: 'fa-solid fa-link',
    iconColor: 'text-purple-600 dark:text-purple-400',
    title: t('charts.process.step3_title'),
    description: t('charts.process.step3_desc'),
    label: t('charts.process.label3'),
    barColor: 'bg-gradient-to-r from-purple-500 to-purple-600',
    iconBg: 'bg-purple-100 dark:bg-purple-900/50',
    badgeColor: 'bg-gradient-to-br from-purple-500 to-purple-700'
  },
  {
    icon: 'fa-solid fa-wand-magic-sparkles',
    iconColor: 'text-green-600 dark:text-green-400',
    title: t('charts.process.step4_title'),
    description: t('charts.process.step4_desc'),
    label: t('charts.process.label4'),
    barColor: 'bg-gradient-to-r from-green-500 to-green-600',
    iconBg: 'bg-green-100 dark:bg-green-900/50',
    badgeColor: 'bg-gradient-to-br from-green-500 to-green-700'
  }
]);
</script>

<style scoped>
</style>
